/**
 * TTS router — the public entry point for the rest of the app.
 *
 * Two complementary paths:
 *
 *   1. cache.get(key)        — IndexedDB hit. Decode WAV → playback. ~10ms.
 *   2. streamPcmFromServer  — POST /v1/audio/speech with stream:true.
 *                              First PCM chunk arrives in ~750ms; we play
 *                              chunks as they land instead of waiting for
 *                              the full WAV. After playback completes we
 *                              also store the assembled WAV in cache so
 *                              repeats are instant.
 *
 * The MOSS-TTS-Nano fallback was removed: the model wasn't producing
 * intelligible English/Spanish (its trained language was a different domain),
 * loading 380MB of ONNX into a Web Worker had stability issues in dev, and
 * Qwen3-TTS handles both languages well enough that a fallback isn't needed
 * for v1. If the server is down, the chat reply still appears as text — only
 * the spoken audio is missing.
 */

import {
  synthesizeViaServer,
  streamPcmFromServer,
  decodeWavBlob,
  detectLanguage,
  STREAM_SAMPLE_RATE,
  type TtsLanguage,
  type SynthesisOutput,
} from './server';
import { cacheKey, cacheGet, cacheSet } from './cache';
import { openStreamPlayer, getStreamingAudioContext } from './streamPlayer';

export type TtsSource = 'cache' | 'server' | 'server-stream' | 'fallback';

export interface RouteResult {
  output: SynthesisOutput;
  source: TtsSource;
  /** Total wall-clock latency from request start to playable audio. */
  latencyMs: number;
}

export interface SynthesizeOptions {
  /** If omitted, language is auto-detected from text content. */
  language?: TtsLanguage;
  /** Caller can preempt long server requests. */
  signal?: AbortSignal;
}

const VOICE_FOR_LANGUAGE: Record<TtsLanguage, string> = {
  English: 'alloy',
  Spanish: 'nova',
};

/* ── Circuit breaker for the home server ────────────────────────────
 *
 * If the server racks up FAILURE_THRESHOLD failures within FAILURE_WINDOW_MS,
 * we mark the circuit "open" for OPEN_DURATION_MS — every TTS request in that
 * window skips the server and fails fast (there is no fallback synth; the
 * reply degrades to text-only). After the window, the circuit becomes
 * "half-open" — one probe request is allowed through. On success the circuit
 * closes (resume normal operation). On failure it re-opens for another
 * OPEN_DURATION_MS.
 *
 * This prevents cascading slow-replies during a server hiccup: if the home
 * box reboots or the tunnel drops, requests fail immediately (the chat reply
 * still streams as text) instead of each one waiting through a 20s timeout.
 */
const FAILURE_THRESHOLD = 3;
const FAILURE_WINDOW_MS = 30_000;
const OPEN_DURATION_MS = 60_000;

interface CircuitState {
  failures: number[]; // timestamps
  openUntil: number;
  halfOpenInFlight: boolean;
}
const circuit: CircuitState = { failures: [], openUntil: 0, halfOpenInFlight: false };

/**
 * Three states, derived rather than stored:
 *   openUntil === 0                 -> CLOSED    (allow everything)
 *   now  <  openUntil               -> OPEN      (allow nothing)
 *   now >= openUntil && openUntil>0 -> HALF-OPEN (allow exactly ONE probe)
 *
 * The half-open rule used to be unenforceable. The old final line was
 * `return now >= circuit.openUntil`, which is true for every caller once the
 * window has elapsed — so the first call set the in-flight flag and every
 * other call fell through and was let past anyway. Against a hard-down server
 * (which is the live situation: the host has returned HTTP 530 for weeks) the
 * breaker therefore stopped almost nothing.
 */
function isCircuitClosed(): boolean {
  const now = Date.now();
  if (circuit.openUntil === 0) return true;          // closed
  if (now < circuit.openUntil) return false;         // open
  if (circuit.halfOpenInFlight) return false;        // a probe is already out
  circuit.halfOpenInFlight = true;                   // this caller IS the probe
  return true;
}

function recordServerSuccess(): void {
  circuit.failures = [];
  circuit.openUntil = 0;
  circuit.halfOpenInFlight = false;
}

function recordServerFailure(): void {
  const now = Date.now();

  // A failed probe re-opens the breaker immediately for a full window. The old
  // code cleared the in-flight flag and pushed onto `failures`, which had been
  // emptied when the breaker opened — so it took THREE more failures to
  // re-open, and in between every request was let through to a dead server.
  if (circuit.halfOpenInFlight) {
    circuit.halfOpenInFlight = false;
    circuit.failures = [];
    circuit.openUntil = now + OPEN_DURATION_MS;
    console.warn(`[ttsRouter] circuit breaker probe failed — OPEN again for ${OPEN_DURATION_MS / 1000}s`);
    return;
  }

  // Drop failures older than the window
  circuit.failures = circuit.failures.filter((t) => now - t < FAILURE_WINDOW_MS);
  circuit.failures.push(now);
  if (circuit.failures.length >= FAILURE_THRESHOLD) {
    circuit.openUntil = now + OPEN_DURATION_MS;
    circuit.failures = [];
    console.warn(`[ttsRouter] circuit breaker OPEN for ${OPEN_DURATION_MS / 1000}s — TTS server unreachable, voice replies will be text-only`);
  }
}

/** Test-only: restore the breaker to its initial closed state. */
export function __resetCircuitForTests(): void {
  circuit.failures = [];
  circuit.openUntil = 0;
  circuit.halfOpenInFlight = false;
}

/** Exposed for debugging/observability. */
export function getCircuitStatus(): { state: 'closed' | 'half-open' | 'open'; openUntilMs: number; failureCount: number } {
  const now = Date.now();
  if (now < circuit.openUntil) return { state: 'open', openUntilMs: circuit.openUntil, failureCount: 0 };
  if (circuit.halfOpenInFlight) return { state: 'half-open', openUntilMs: 0, failureCount: 0 };
  return { state: 'closed', openUntilMs: 0, failureCount: circuit.failures.length };
}

/**
 * Resolve text → SynthesisOutput via cache → server. There is no fallback
 * synth (see header comment): if the cache misses and the server fails —
 * or the circuit is open — this throws so callers can degrade the reply
 * to text-only.
 */
export async function synthesize(text: string, opts: SynthesizeOptions = {}): Promise<RouteResult> {
  const start = performance.now();
  const language = opts.language ?? detectLanguage(text);
  const voice = VOICE_FOR_LANGUAGE[language];
  const key = await cacheKey(language, voice, text);
  const tag = `"${text.slice(0, 30)}${text.length > 30 ? '…' : ''}"`;

  // 1. Cache
  try {
    const cached = await cacheGet(key);
    if (cached) {
      const output = await decodeWavBlob(cached);
      const dt = (performance.now() - start).toFixed(0).padStart(5);
      console.info(`[ttsTiming] +${dt}ms synth CACHE HIT ${tag}`);
      return { output, source: 'cache', latencyMs: performance.now() - start };
    }
  } catch (err) {
    console.warn('[ttsRouter] cache lookup failed (continuing to server)', err);
  }

  // 2. Server (skipped if circuit is open)
  if (isCircuitClosed()) {
    try {
      console.info(`[ttsTiming] +${(performance.now() - start).toFixed(0).padStart(5)}ms synth SERVER FETCH START ${tag}`);
      const { wav, output } = await synthesizeViaServer(text, language, opts.signal);
      const dt = (performance.now() - start).toFixed(0).padStart(5);
      const audioSec = (output.audio.length / output.numChannels / output.sampleRate).toFixed(2);
      console.info(`[ttsTiming] +${dt}ms synth SERVER WAV READY ${tag} (${audioSec}s of audio, ${(wav.size / 1024).toFixed(0)}KB)`);
      recordServerSuccess();
      cacheSet(key, wav, language, text).catch((e) => console.warn('[ttsRouter] cache set failed', e));
      return { output, source: 'server', latencyMs: performance.now() - start };
    } catch (err) {
      if (opts.signal?.aborted) throw err;
      recordServerFailure();
      console.warn('[ttsRouter] TTS server unavailable — no audio for this turn', err);
    }
  } else {
    console.debug('[ttsRouter] circuit open, skipping server');
  }

  // No MOSS fallback — surface the failure so callers can show "no audio"
  throw new Error('TTS server unavailable and no fallback configured');
}

/**
 * High-level "synthesize and play" — non-streaming variant. Used by the
 * /tts-test page for cache verification. For real conversational use prefer
 * `streamingSpeak` which has a much lower TTFB.
 */
export async function speak(text: string, opts: SynthesizeOptions = {}): Promise<RouteResult> {
  const result = await synthesize(text, opts);
  await playPCM(result.output);
  return result;
}

/**
 * Play an already-resolved SynthesisOutput on the shared streaming
 * AudioContext, with contiguous scheduling and edge fades. Used by the
 * sentence-level orchestrator to separate fetch (parallel) from playback
 * (strictly sequential): we kick off all sentence syntheses in parallel
 * as Gemini emits them, and play their audio in order via this function.
 */
export async function playSynthesisOutput(output: SynthesisOutput, signal?: AbortSignal): Promise<void> {
  return playFullBufferWithAbort(output, signal);
}

/**
 * Play a SynthesisOutput as a single AudioBufferSourceNode on the shared
 * 24kHz streaming context. Used by cache hits — the audio is fully decoded
 * up front so there's no streaming/jitter concern; one source = no chunk
 * boundaries to glitch on.
 */
async function playPCM(out: SynthesisOutput): Promise<void> {
  const ctx = getStreamingAudioContext();
  if (ctx.state === 'suspended') await ctx.resume();
  const numFrames = out.audio.length / out.numChannels;
  const buffer = ctx.createBuffer(out.numChannels, numFrames, out.sampleRate);
  for (let ch = 0; ch < out.numChannels; ch++) {
    const channelData = buffer.getChannelData(ch);
    for (let s = 0; s < numFrames; s++) {
      channelData[s] = out.audio[s * out.numChannels + ch];
    }
  }
  return new Promise((resolve) => {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = () => resolve();
    source.start(0);
  });
}

export interface SpeakStreamingOptions extends SynthesizeOptions {
  onFirstAudio?: (latencyMs: number) => void;
  onPlayingComplete?: () => void;
}

/**
 * Speak `text`. The path the rest of the app should call for chat replies.
 *
 * Resolution:
 *   1. IndexedDB cache → decode WAV → play. ~10-20 ms to first audio.
 *   2. Server full WAV → cache → play. ~4-5 s wait, then smooth playback.
 *
 * Streaming PCM is intentionally NOT used here. It worked in isolated
 * curl tests (TTFB ~240 ms) but in real conversational use the audio
 * chunks arriving slower than realtime (server RTF ~1.27 without
 * flash-attention) caused frequent buffer underruns. Even with a 1.2-s
 * AudioWorklet jitter buffer, replies longer than ~5 s would drain the
 * buffer and the user heard rapid on/off cuts mid-word. The worklet
 * scaffolding (services/tts/streamPlayer.ts, public/tts-stream-worklet.js)
 * stays in the tree so we can re-enable streaming once flash-attention 2
 * is installed on the server (which would push RTF below 1 and remove
 * the deficit).
 */
export async function streamingSpeak(text: string, opts: SpeakStreamingOptions = {}): Promise<{ source: TtsSource; latencyMs: number; ttfaMs: number }> {
  const start = performance.now();
  const language = opts.language ?? detectLanguage(text);
  const voice = VOICE_FOR_LANGUAGE[language];
  const key = await cacheKey(language, voice, text);

  // 1. Cache
  try {
    const cached = await cacheGet(key);
    if (cached) {
      const output = await decodeWavBlob(cached);
      const ttfaMs = performance.now() - start;
      opts.onFirstAudio?.(ttfaMs);
      await playFullBufferWithAbort(output, opts.signal);
      opts.onPlayingComplete?.();
      return { source: 'cache', latencyMs: performance.now() - start, ttfaMs };
    }
  } catch (err) {
    console.warn('[ttsRouter] cache lookup failed', err);
  }

  if (!isCircuitClosed()) {
    opts.onPlayingComplete?.();
    throw new Error('TTS server circuit open and no fallback configured');
  }

  // 2. Server full WAV
  console.debug(`[ttsRouter] cache miss; lang=${language} voice=${voice} text="${text.slice(0, 80)}${text.length > 80 ? '…' : ''}"`);
  try {
    const { wav, output } = await synthesizeViaServer(text, language, opts.signal);
    recordServerSuccess();
    cacheSet(key, wav, language, text).catch((e) => console.warn('[ttsRouter] cache set failed', e));
    const ttfaMs = performance.now() - start;
    opts.onFirstAudio?.(ttfaMs);
    console.debug(`[ttsRouter] WAV fetched in ${ttfaMs.toFixed(0)}ms, ${output.numFrames} frames @ ${output.sampleRate}Hz; starting playback`);
    await playFullBufferWithAbort(output, opts.signal);
    console.debug('[ttsRouter] playback complete');
    opts.onPlayingComplete?.();
    return { source: 'server', latencyMs: performance.now() - start, ttfaMs };
  } catch (err) {
    if (opts.signal?.aborted) {
      console.debug('[ttsRouter] aborted by caller');
      opts.onPlayingComplete?.();
      throw err;
    }
    recordServerFailure();
    console.error('[ttsRouter] server fetch / playback failed:', err);
    opts.onPlayingComplete?.();
    throw err;
  }
}

interface StreamPlayResult {
  ttfaMs: number;
  assembledWav: Blob | null;
}

/**
 * Consume PCM chunks from the server and feed them into an AudioWorklet-backed
 * player. The worklet maintains its own ring buffer and outputs samples one at
 * a time, so chunk boundaries are sample-perfect and underruns play silence
 * (no clicks, no resampling artifacts, no scheduling drift).
 *
 * Why a worklet instead of one AudioBufferSourceNode per chunk:
 *  - Web Audio resamples each buffer independently when the buffer rate
 *    differs from the context rate; the resampler resets at every chunk →
 *    audible discontinuity at chunk boundaries.
 *  - When the server is slower than realtime (RTF > 1), nextStartTime falls
 *    behind currentTime and the Math.max guard slips forward → silent gaps
 *    mid-word.
 *  - Sub-sample scheduling drift accumulates over a long reply.
 *
 * The worklet processor is loaded from /public/tts-stream-worklet.js and
 * the AudioContext is forced to the server's PCM rate (24 kHz) so no
 * resampling happens at all. See streamPlayer.ts for the JS-side API.
 */
async function streamAndPlay(
  text: string,
  language: TtsLanguage,
  opts: SpeakStreamingOptions,
  startedAt: number,
): Promise<StreamPlayResult> {
  let firstAudioFired = false;
  let ttfaMs = 0;
  const collected: Float32Array[] = [];

  let underrunCount = 0;
  const player = await openStreamPlayer({
    onStarted: () => {
      if (!firstAudioFired) {
        firstAudioFired = true;
        ttfaMs = performance.now() - startedAt;
        opts.onFirstAudio?.(ttfaMs);
        console.debug(`[ttsRouter] streaming TTFA=${ttfaMs.toFixed(0)}ms`);
      }
    },
    onUnderrun: () => {
      underrunCount++;
      console.debug(`[ttsRouter] worklet underrun #${underrunCount} (server slower than playback for this stretch)`);
    },
  });

  // Wire abort: flush + disconnect the worklet immediately
  const onAbort = () => player.abort();
  if (opts.signal) {
    if (opts.signal.aborted) {
      player.abort();
      throw new DOMException('aborted', 'AbortError');
    }
    opts.signal.addEventListener('abort', onAbort, { once: true });
  }

  try {
    for await (const float32 of streamPcmFromServer(text, language, opts.signal)) {
      if (opts.signal?.aborted) throw new DOMException('aborted', 'AbortError');
      if (float32.length === 0) continue;
      // Keep a copy for cache write before transferring ownership to worklet.
      collected.push(new Float32Array(float32));
      player.pushChunk(float32);
    }
    player.end();
    await player.finished;
  } catch (err) {
    player.abort();
    throw err;
  } finally {
    if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
  }

  return {
    ttfaMs,
    assembledWav: collected.length > 0 ? floatChunksToWav(collected, STREAM_SAMPLE_RATE) : null,
  };
}

/**
 * Tracker for back-to-back sentence playback. When a TTS call finishes,
 * we record the AudioContext time at which playback ended; the next call
 * picks up from there so there's NO gap between sentences.
 *
 * Note: we DON'T need a reset timer. The Math.max(nextStartTime, now+0.01)
 * guard already handles stale state safely — if a new conversation starts
 * 100 seconds later, nextStartTime is 100s in the past so max() falls back
 * to now+0.01. The timer-based reset that lived here previously was active-
 * harmful: it fired during long sentence playback and broke contiguity for
 * the next sentence (gap 4-12s instead of 0).
 */
const continuousPlayback = {
  nextStartTime: 0 as number,   // ctx.currentTime when next source should start
};

/**
 * Apply a fade-in at the start and fade-out at the end of an interleaved
 * Float32 PCM buffer. Eliminates click/pop at sentence boundaries when
 * multiple AudioBufferSourceNodes are scheduled back-to-back. With our
 * AudioContext now forced to 24 kHz when possible, the resampler-reset
 * artifact is gone, but the fades stay as a defensive belt: even if a
 * browser falls back to 48 kHz default rate, the fade hides the resample
 * boundary inside an inaudibly short ramp.
 *
 * 20 ms is well below the threshold of perception for human hearing for
 * a brief edge ramp, but ample time for any DSP transient to settle.
 */
function applyEdgeFades(audio: Float32Array, numChannels: number, sampleRate: number, fadeMs = 20): void {
  const fadeFrames = Math.min(
    Math.floor((fadeMs / 1000) * sampleRate),
    Math.floor((audio.length / numChannels) / 4),
  );
  if (fadeFrames < 4) return;
  for (let f = 0; f < fadeFrames; f++) {
    const gain = f / fadeFrames;
    for (let ch = 0; ch < numChannels; ch++) {
      audio[f * numChannels + ch] *= gain;
      audio[(audio.length / numChannels - 1 - f) * numChannels + ch] *= gain;
    }
  }
}

async function playFullBufferWithAbort(output: SynthesisOutput, signal?: AbortSignal): Promise<void> {
  // ensureContextRunning auto-rebuilds the context if it gets stuck after
  // resume() — this handles the Chrome bug we previously worked around by
  // dropping the forced 24kHz rate. Now we get 24kHz when it works AND
  // graceful fallback when it doesn't.
  const { ensureContextRunning } = await import('./streamPlayer');
  const ctx = await ensureContextRunning();
  if (signal?.aborted) return;

  const numFrames = output.audio.length / output.numChannels;
  // Apply edge fades BEFORE filling the buffer so the AudioBuffer holds
  // the smoothed samples. This mutates the input Float32; that's fine,
  // it's a local copy from the WAV decoder.
  applyEdgeFades(output.audio, output.numChannels, output.sampleRate);

  const buffer = ctx.createBuffer(output.numChannels, numFrames, output.sampleRate);
  for (let ch = 0; ch < output.numChannels; ch++) {
    const channelData = buffer.getChannelData(ch);
    for (let s = 0; s < numFrames; s++) {
      channelData[s] = output.audio[s * output.numChannels + ch];
    }
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);

  // Schedule contiguously with the previous sentence: if its scheduled end
  // is still in the future, start the new source at EXACTLY that moment so
  // audio is sample-perfect contiguous. Only add a lookahead when the
  // previous sentence has already ended (or there is no previous) — without
  // a lookahead the audio thread doesn't have time to pick up "now".
  const now = ctx.currentTime;
  const startAt = continuousPlayback.nextStartTime > now
    ? continuousPlayback.nextStartTime
    : now + 0.005;
  const gapFromPrevious = Math.max(0, startAt - continuousPlayback.nextStartTime);
  continuousPlayback.nextStartTime = startAt + buffer.duration;

  console.info(`[ttsTiming]       PLAY SCHEDULED at ctx.currentTime=${now.toFixed(2)}s, startAt=${startAt.toFixed(2)}s, dur=${buffer.duration.toFixed(2)}s, gapFromPrev=${(gapFromPrevious * 1000).toFixed(0)}ms`);

  await new Promise<void>((resolve) => {
    source.onended = () => {
      console.info(`[ttsTiming]       PLAY ENDED ctx=${ctx.currentTime.toFixed(2)}s`);
      resolve();
    };
    if (signal) {
      signal.addEventListener('abort', () => {
        try { source.stop(); } catch { /* ignore */ }
        // Rewind the schedule to the moment playback actually stopped.
        //
        // nextStartTime was optimistically advanced to the clip's natural END
        // above. Barge in one second into an eight-second reply and it stays
        // ~7s in the future, so the NEXT turn's first chunk is scheduled into
        // that dead window and the user hears silence for the remainder before
        // the answer begins. Clamping to `now` also means we never move the
        // marker forward, so contiguous playback of a normal sequence is
        // unaffected.
        continuousPlayback.nextStartTime = Math.min(
          continuousPlayback.nextStartTime,
          ctx.currentTime,
        );
        resolve();
      }, { once: true });
    }
    source.start(startAt);
  });
}

/** Wrap mono Float32 chunks in a 16-bit PCM WAV blob for IndexedDB caching. */
function floatChunksToWav(chunks: Float32Array[], sampleRate: number): Blob {
  const totalSamples = chunks.reduce((sum, c) => sum + c.length, 0);
  const dataSize = totalSamples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF'); view.setUint32(4, 36 + dataSize, true); writeStr(8, 'WAVE');
  writeStr(12, 'fmt '); view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  writeStr(36, 'data'); view.setUint32(40, dataSize, true);

  let offset = 44;
  for (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i++) {
      let s = chunk[i];
      if (s > 1) s = 1; else if (s < -1) s = -1;
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Synthesize and return as a WAV blob. Used by call sites that hand audio
 * off to HTMLAudioElement (legacy path; conversational code paths should
 * use streamingSpeak instead).
 */
export async function synthesizeWav(text: string, opts: SynthesizeOptions = {}): Promise<{ wav: Blob; source: TtsSource; latencyMs: number }> {
  const start = performance.now();
  const language = opts.language ?? detectLanguage(text);
  const voice = VOICE_FOR_LANGUAGE[language];
  const key = await cacheKey(language, voice, text);

  try {
    const cached = await cacheGet(key);
    if (cached) return { wav: cached, source: 'cache', latencyMs: performance.now() - start };
  } catch (err) {
    console.warn('[ttsRouter] cache lookup failed', err);
  }

  const { wav } = await synthesizeViaServer(text, language, opts.signal);
  cacheSet(key, wav, language, text).catch((e) => console.warn('[ttsRouter] cache set failed', e));
  return { wav, source: 'server', latencyMs: performance.now() - start };
}

/** Re-export for convenience so call sites don't have to import from server.ts. */
export { detectLanguage } from './server';
export type { TtsLanguage } from './server';
